import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, Shield, Zap, Clock, MessageCircle, Phone, CheckCircle } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';
import { isAuthenticated } from '../../../auth/auth';

const platformIconsImage = '/assets/images/ChatGPT_Image_Jul_16__2026__08_38_06_PM-1784214549803.png';

const trustFeatures = [
  { icon: MessageCircle, label: 'Human-like conversations' },
  { icon: Zap, label: 'Automate support & sales' },
  { icon: Clock, label: '24/7 availability' },
];

const AuthCard = () => {
  const [isSignup, setIsSignup] = useState(true);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [justSignedUp, setJustSignedUp] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || '/analytics-overview';

  useEffect(() => {
    if (isAuthenticated() && !justSignedUp) {
      navigate(redirectTo, { replace: true });
    }
  }, [navigate, redirectTo, justSignedUp]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/api/v1/merchant/user/login', { email, password });
      const token = res?.data?.data?.token;
      if (!token) throw new Error('Invalid login response');
      localStorage.setItem('authToken', token);
      window.dataLayer?.push({ event: 'login' });
      const isNew = res?.data?.data?.isNewUser;
      if (isNew) { setJustSignedUp(true); navigate('/agent-performance', { replace: true }); }
      else { navigate(redirectTo, { replace: true }); }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Invalid email or password');
      } else if (err.response?.status === 403) {
        setError('Access denied');
      } else {
        setError(err.response?.data?.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/api/v1/merchant/user/signup', { name: fullName, email, password });
      const token = res?.data?.data?.token;
      if (!token) throw new Error('Invalid signup response');
      localStorage.setItem('authToken', token);
      if (typeof fbq === 'function') fbq('track', 'CompleteRegistration', { content_name: 'EmailSignup', content_category: 'SignupPage' });
      window.dataLayer?.push({ event: 'signup_email' });
      const isNew = res?.data?.data?.isNewUser;
      if (isNew) { setJustSignedUp(true); navigate('/agent-performance', { replace: true }); }
      else { navigate(redirectTo, { replace: true }); }
    } catch (err) {
      if (err.response?.status === 409) {
        setError('An account with this email already exists');
      } else {
        setError(err.response?.data?.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/api/v1/merchant/user/google-login', {
        idToken: credentialResponse.credential,
      });
      const token = res?.data?.data?.token;
      if (!token) throw new Error('Invalid response');
      localStorage.setItem('authToken', token);
      const isNew = res?.data?.data?.isNewUser;
      if (isNew && typeof fbq === 'function') fbq('track', 'CompleteRegistration', { content_name: 'GoogleSignup', content_category: 'SignupPage' });
      window.dataLayer?.push({ event: 'signup_google', is_new: isNew });
      if (isNew) { setJustSignedUp(true); navigate('/agent-performance', { replace: true }); }
      else { navigate(redirectTo, { replace: true }); }
    } catch (err) {
      setError(err.response?.data?.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="w-full rounded-3xl p-8 relative"
      style={{
        background: '#fff',
        boxShadow: '0 24px 80px rgba(15,29,82,0.13), 0 4px 24px rgba(79,107,255,0.08)',
        border: '1px solid #EAECEF',
        animation: 'floatCard 4s ease-in-out infinite',
      }}
    >
      <style>{`
        @keyframes floatCard {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-1" style={{ color: '#111827' }}>
          {isSignup ? 'Create an Account' : 'Welcome Back'}
        </h2>
        <p className="text-sm" style={{ color: '#6B7280' }}>
          {isSignup ? 'Sign up to get started with Calltify' : 'Log in to access your Calltify dashboard'}
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-xl px-4 py-2.5 text-sm" style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626' }}>
          {error}
        </div>
      )}

      <form onSubmit={isSignup ? handleSignup : handleLogin}>
        <div className="space-y-4 mb-5">
          {isSignup && (
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#374151' }}>Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={e => setFullName(e?.target?.value)}
                placeholder="Mohit Singh"
                required
                className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                style={{
                  border: '1.5px solid #EAECEF',
                  background: '#FAFBFF',
                  color: '#111827',
                }}
                onFocus={e => e.target.style.borderColor = '#4F6BFF'}
                onBlur={e => e.target.style.borderColor = '#EAECEF'}
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#374151' }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e?.target?.value)}
              placeholder="you@company.com"
              required
              className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
              style={{
                border: '1.5px solid #EAECEF',
                background: '#FAFBFF',
                color: '#111827',
              }}
              onFocus={e => e.target.style.borderColor = '#4F6BFF'}
              onBlur={e => e.target.style.borderColor = '#EAECEF'}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#374151' }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e?.target?.value)}
              placeholder="••••••••"
              required
              className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
              style={{
                border: '1.5px solid #EAECEF',
                background: '#FAFBFF',
                color: '#111827',
              }}
              onFocus={e => e.target.style.borderColor = '#4F6BFF'}
              onBlur={e => e.target.style.borderColor = '#EAECEF'}
            />
          </div>
        </div>
        {!isSignup && (
          <div className="flex items-center justify-between mb-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={remember}
                onChange={e => setRemember(e?.target?.checked)}
                className="w-4 h-4 rounded"
                style={{ accentColor: '#4F6BFF' }}
              />
              <span className="text-sm" style={{ color: '#6B7280' }}>Remember me</span>
            </label>
            <a href="#" className="text-sm font-medium transition-colors" style={{ color: '#4F6BFF' }}>Forgot Password?</a>
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3.5 rounded-xl font-semibold text-sm text-white mb-5 transition-all duration-250"
          style={{
            background: loading ? '#6B7280' : 'linear-gradient(135deg, #0F1D52 0%, #1a2d6b 100%)',
            boxShadow: loading ? 'none' : '0 4px 16px rgba(15,29,82,0.3)',
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
          onMouseEnter={e => { if (!loading) { e.target.style.transform = 'translateY(-3px)'; e.target.style.boxShadow = '0 8px 24px rgba(15,29,82,0.4)'; } }}
          onMouseLeave={e => { if (!loading) { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 16px rgba(15,29,82,0.3)'; } }}
        >
          {loading
            ? (isSignup ? 'Creating account...' : 'Logging in...')
            : (isSignup ? 'Create Account' : 'Log in to Calltify')
          }
        </button>
      </form>

      <div className="flex items-center gap-3 mb-5">
        <div className="flex-1 h-px" style={{ background: '#EAECEF' }} />
        <span className="text-xs" style={{ color: '#9CA3AF' }}>Or continue with</span>
        <div className="flex-1 h-px" style={{ background: '#EAECEF' }} />
      </div>
      <div className="mb-6 flex justify-center">
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={() => setError('Google sign-in failed')}
          width="100%"
          text="continue_with"
          shape="rectangular"
          theme="outline"
        />
      </div>
      <p className="text-center text-sm" style={{ color: '#6B7280' }}>
        {isSignup ? (
          <>
            Already have an account?{' '}
            <button onClick={() => { setIsSignup(false); setError(null); }} className="font-semibold transition-colors" style={{ color: '#4F6BFF', background: 'none', border: 'none', cursor: 'pointer' }}>
              Log in
            </button>
          </>
        ) : (
          <>
            Don't have an account?{' '}
            <button onClick={() => { setIsSignup(true); setError(null); }} className="font-semibold transition-colors" style={{ color: '#4F6BFF', background: 'none', border: 'none', cursor: 'pointer' }}>
              Create an account
            </button>
          </>
        )}
      </p>
    </div>
  );
};

const HeroSection = () => {
  const [phone, setPhone] = useState('');
  const [calling, setCalling] = useState(false);
  const [callStatus, setCallStatus] = useState(null);
  const [showToast, setShowToast] = useState(false);

  const handleTryAgent = async () => {
    setCalling(true);
    setCallStatus(null);
    setShowToast(false);
    try {
      await api.post('/api/v1/test-agent-session', {
        agent_id: '0180109b-5cd4-4d47-b329-47507fb43b46',
        to_number: '+91' + phone,
        direction: 'outbound',
        dynamic_vars: { graph_enabled: true },
      });
      if (typeof fbq === 'function') fbq('track', 'Contact', { content_name: 'TryAgentCall', content_category: 'SignupPage' });
      window.dataLayer?.push({ event: 'try_agent', phone: phone });
      setCallStatus('success');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch {
      setCallStatus('error');
    } finally {
      setCalling(false);
    }
  };

  return (
    <section className="relative overflow-hidden" style={{ background: '#FAFBFF', minHeight: '100vh' }}>
      {/* Subtle background gradient blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, #4F6BFF 0%, transparent 70%)' }} />
        <div className="absolute top-1/2 -right-32 w-80 h-80 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #6E5CFF 0%, transparent 70%)' }} />
      </div>
      <div className="relative mx-auto px-6 lg:px-8 py-8" style={{ maxWidth: '1280px' }}>
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">

          {/* LEFT COLUMN — 58% */}
          <div className="w-full lg:w-[58%] flex flex-col gap-8 pt-4">

            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="overflow-hidden" style={{ width: '36px', height: '36px' }}>
                <img
                  src="/assets/images/Logo-Calltify-(semibold)_1.png"
                  alt="Calltify logo"
                  className="h-9 object-cover object-left"
                />
              </div>
              <span className="text-xl font-bold" style={{ color: '#0F1D52' }}>Calltify</span>
            </div>

            {/* Caption */}
            <div>
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(79,107,255,0.08)', color: '#4F6BFF', border: '1px solid rgba(79,107,255,0.2)' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                AI-Powered Customer Support
              </span>
            </div>

            {/* Platform Icons */}
            <div className="flex items-center gap-3 flex-wrap">
              <img
                src="/assets/images/ChatGPT_Image_Jul_16__2026__08_38_06_PM_V2-1784215162340.png"
                alt="Platform integrations including WhatsApp, Chrome, Chat, Voice, and Phone"
                className="h-12 w-auto object-contain"
              />
            </div>

            {/* Headline */}
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight tracking-tight" style={{ color: '#111827' }}>
                Conversational intelligence
                <br />
                to{' '}
                <span
                  className="relative"
                  style={{
                    background: 'linear-gradient(135deg, #5A7BFF 0%, #6E5CFF 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  10x your business
                </span>
              </h1>
              <p className="mt-4 text-lg leading-relaxed" style={{ color: '#6B7280', maxWidth: '520px' }}>
                AI agents that deliver outcomes you want across Voice, Chat, WhatsApp and Web.
              </p>
            </div>

            {/* CTA Search Bar */}
            <div className="relative">
              {/* Success Toast — overlaid above call section */}
              {showToast && (
                <div className="absolute -top-12 left-1/2 -translate-x-1/2 z-10">
                  <div
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl shadow-lg text-sm font-semibold whitespace-nowrap animate-in slide-in-from-top-2 fade-in"
                    style={{ background: '#16a34a', color: '#ffffff' }}
                  >
                    <CheckCircle size={18} />
                    Call placed successfully!
                  </div>
                </div>
              )}
            <div
              className="flex items-center rounded-2xl overflow-hidden"
              style={{ background: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}
            >
              <div className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3.5 flex-shrink-0">
                <span className="text-lg sm:text-lg">🇮🇳</span>
                <span className="text-base sm:text-sm font-medium" style={{ color: '#374151' }}>+91</span>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={e => {
                  const val = e?.target?.value?.replace(/\D/g, '');
                  if (val.length <= 10) setPhone(val);
                }}
                maxLength={10}
                placeholder="Enter 10 digit mobile number"
                className="flex-1 min-w-0 px-1 sm:px-4 py-3.5 text-base sm:text-sm outline-none bg-transparent border-0"
                style={{ color: '#111827' }}
              />
              <button
                disabled={phone.length !== 10 || calling}
                onClick={handleTryAgent}
                className="flex items-center justify-center gap-2 px-3 sm:px-5 py-3 sm:py-3.5 m-1.5 rounded-xl text-sm font-semibold text-white transition-all duration-250 flex-shrink-0"
                style={{
                  background: (phone.length !== 10 || calling) ? '#9CA3AF' : '#0F1D52',
                  boxShadow: (phone.length !== 10 || calling) ? 'none' : '0 2px 8px rgba(15,29,82,0.3)',
                  cursor: (phone.length !== 10 || calling) ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={e => { if (phone.length === 10 && !calling) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(15,29,82,0.4)'; } }}
                onMouseLeave={e => { if (phone.length === 10 && !calling) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(15,29,82,0.3)'; } }}
              >
                <Phone size={20} />
                <span className="hidden sm:inline">
                  {calling ? 'Calling...' : callStatus === 'error' ? 'Failed, retry' : 'Try an agent'}
                </span>
              </button>
            </div>
            </div>

            {/* Free Trial Card */}
            <div
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-2xl p-5"
              style={{ background: '#fff', border: '1.5px solid #EAECEF', boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}
            >
              <div>
                <p className="text-sm mb-1" style={{ color: '#6B7280' }}>Start your trial today and get</p>
                <p className="text-3xl font-bold" style={{ color: '#0F1D52' }}>₹500 BONUS CREDIT</p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {['On Recharge of ₹10,000', 'Instant access']?.map(badge => (
                    <span key={badge} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: 'rgba(79,107,255,0.08)', color: '#4F6BFF' }}>
                      <span className="w-1 h-1 rounded-full bg-current" />
                      {badge}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-col items-start sm:items-end gap-2 flex-shrink-0">
                <button
                  onClick={() => { window.dataLayer?.push({ event: 'start_free_trial_click' }); document.getElementById('auth-card')?.scrollIntoView({ behavior: 'smooth' }); }}
                  className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-white transition-all duration-250"
                  style={{ background: '#0F1D52', boxShadow: '0 2px 8px rgba(15,29,82,0.3)' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(15,29,82,0.4)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(15,29,82,0.3)'; }}
                >
                  Start Trial
                  <ArrowRight size={14} />
                </button>
                <span className="text-xs" style={{ color: '#9CA3AF' }}>Create your agent now!</span>
              </div>
            </div>

            {/* Trust Features */}
            <div className="flex items-center gap-8 flex-wrap">
              {trustFeatures?.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2.5 group cursor-pointer">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-250"
                    style={{ border: '1.5px solid #4F6BFF', background: 'rgba(79,107,255,0.06)' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                  >
                    <Icon size={14} color="#4F6BFF" />
                  </div>
                  <span className="text-sm font-medium" style={{ color: '#374151' }}>{label}</span>
                </div>
              ))}
            </div>

            {/* Footer Row */}
            {/* <div className="flex flex-col gap-4 pt-2 border-t" style={{ borderColor: '#EAECEF' }}>
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-xs" style={{ color: '#9CA3AF' }}>© 2026 Calltify</span>
                <a href="#" className="text-xs transition-colors" style={{ color: '#9CA3AF' }} onMouseEnter={e => e.target.style.color = '#4F6BFF'} onMouseLeave={e => e.target.style.color = '#9CA3AF'}>Privacy Policy</a>
                <a href="#" className="text-xs transition-colors" style={{ color: '#9CA3AF' }} onMouseEnter={e => e.target.style.color = '#4F6BFF'} onMouseLeave={e => e.target.style.color = '#9CA3AF'}>Terms</a>
              </div>
              <div className="flex items-center gap-6 flex-wrap">
                {['Built for scaling brands', 'Enterprise-grade security', '24/7 support automation']?.map(badge => (
                  <div key={badge} className="flex items-center gap-1.5">
                    <Shield size={11} color="#4F6BFF" />
                    <span className="text-xs" style={{ color: '#6B7280' }}>{badge}</span>
                  </div>
                ))}
              </div>
            </div> */}
          </div>

          {/* RIGHT COLUMN — 42% */}
          <div id="auth-card" className="w-full lg:w-[42%] lg:sticky lg:top-8 pt-4">
            <AuthCard />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
